import React, { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import classNames from "classnames";
import { useDidUpdate } from "hooks";
import { useDataRoomsActions, usePlatformsSelect } from "store";
import { IModals } from "store/types/modals";

import { LoginMobile } from "./LoginMobile";

import "./RoomConnect.styles.scss";

type Props = {
  onClose: () => void;
} & IModals["dataroomConnect"];

export const RoomConnectMobile: React.FC<Props> = ({ isOpen, onClose, afterSubmit, platforms: platformsFromProps }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<number>(1);
  const [platformId, setPlatformId] = useState<string | null>(null);
  const [platformName, setPlatformName] = useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<{ [key: string]: string | boolean | null } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const { setConnectMessageAction } = useDataRoomsActions();
  const cachedPlatforms = usePlatformsSelect();

  // Use platforms from props (fetched before modal opens) > cached
  const platforms = platformsFromProps || cachedPlatforms;

  // Find the selected platform
  const selectedPlatformData = useMemo(() => {
    if (!platformId || !platforms) return null;
    return platforms.find((p) => p.id === platformId);
  }, [platformId, platforms]);

  const filteredPlatforms = useMemo(() => {
    if (!platforms || !searchTerm) return platforms;
    const searchLower = searchTerm.toLowerCase();
    return platforms.filter((platform) => platform.name.toLowerCase().includes(searchLower));
  }, [platforms, searchTerm]);

  useDidUpdate(() => {
    setConnectMessageAction(null);
    if (isOpen) {
      // Trigger animation by setting open to true after mount
      const timer = setTimeout(() => setOpen(true), 10);
      return () => clearTimeout(timer);
    } else {
      setOpen(false);
      if (step !== 1) {
        setStep(1);
      }
      if (platformId) {
        setPlatformId(null);
        setPlatformName(null);
      }
      setSearchTerm("");
    }
  }, [isOpen]);

  const handleClose = () => {
    setOpen(false);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 250);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open && isOpen}
      onClose={handleClose}
      transitionDuration={300}
      sx={{
        zIndex: 1300,
        "& .MuiDrawer-paper": {
          height: "90vh",
          maxHeight: "90vh",
          borderTopLeftRadius: "20px",
          borderTopRightRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ffffff"
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(10, 49, 66, 0.6)"
        }
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: "40px",
          height: "4px",
          backgroundColor: "#dae0e2",
          borderRadius: "2px",
          margin: "8px auto",
          flexShrink: 0
        }}
      />

      {/* Mobile Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: "56px",
          px: "16px",
          bgcolor: "#EDF1F9",
          borderBottom: "1px solid #dae0e2",
          flexShrink: 0
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 500,
            color: "#0A3142",
            lineHeight: "28px"
          }}
        >
          Connect to Data Room
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            padding: "8px",
            color: "#0A3142"
          }}
        >
          <CloseIcon sx={{ fontSize: "24px" }} />
        </IconButton>
      </Stack>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10
          }}
        >
          <CircularProgress size={40} sx={{ color: "#0A3142" }} />
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          backgroundColor: "#ffffff",
          padding: "20px 16px",
          "&::-webkit-scrollbar": {
            width: "4px"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#898989",
            borderRadius: "2px"
          }
        }}
      >
        <div className="room-connect-mobile">
          <div className="room-connect-mobile__body">
            <div
              className={classNames("step", {
                "step--active": step === 1
              })}
            >
              <div className="step__content">
                <div className="step__title" onClick={() => setStep(1)}>
                  <span>Select platform</span>
                  {selectedPlatformData && (
                    <div className="selected-platform-display">
                      <img
                        src={selectedPlatformData.logo}
                        alt={selectedPlatformData.name}
                        className="selected-platform-logo"
                      />
                      <span className="selected-platform-name">{selectedPlatformData.name}</span>
                    </div>
                  )}
                </div>
                {step === 1 && (
                  <>
                    <div className="platform-search">
                      <TextField
                        placeholder="Search platforms..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "transparent",
                            border: "1px solid transparent",
                            borderRadius: "6px",
                            color: "rgba(10, 49, 66, 0.7)",
                            transition: "all 0.3s ease-in-out",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.15)",
                              borderColor: "rgba(186, 199, 206, 0.4)"
                            },
                            "&.Mui-focused": {
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              borderColor: "rgba(186, 199, 206, 0.6)",
                              color: "rgba(10, 49, 66, 0.9)",
                              cursor: "text !important"
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none"
                            }
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            fontSize: "14px",
                            cursor: "pointer",
                            "&:focus": {
                              cursor: "text !important"
                            },
                            "&::placeholder": {
                              color: "rgba(10, 49, 66, 0.4)",
                              opacity: 1
                            }
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  color: "rgba(10, 49, 66, 0.5)",
                                  fontSize: "20px",
                                  transition: "color 0.2s ease-in-out"
                                }}
                              />
                            </InputAdornment>
                          )
                        }}
                        onFocus={(e) => {
                          const icon = e.currentTarget.querySelector(".MuiInputAdornment-root .MuiSvgIcon-root");
                          if (icon) {
                            (icon as HTMLElement).style.color = "#0a3142";
                          }
                        }}
                        onBlur={(e) => {
                          const icon = e.currentTarget.querySelector(".MuiInputAdornment-root .MuiSvgIcon-root");
                          if (icon) {
                            (icon as HTMLElement).style.color = "rgba(10, 49, 66, 0.5)";
                          }
                        }}
                      />
                    </div>
                    <div className="step__body">
                      <div className="cards-grid-mobile">
                        {filteredPlatforms?.map((item) => (
                          <div
                            onClick={() => {
                              if (platformId === item.id) {
                                setStep(2);
                              } else {
                                setPlatformName(item.name);
                                setPlatformId(item.id);
                                setSelectedPlatform(item.extra);
                                setStep(2);
                              }
                            }}
                            key={item.id}
                            className={classNames("card", {
                              "card--selected": platformId === item.id
                            })}
                          >
                            <img src={item.logo} className="card__logo" alt={item.name} />
                            <span className="card__titile">{item.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="step__footer">
                      <Button
                        onClick={() => setStep(2)}
                        disabled={!platformId}
                        variant="contained"
                        fullWidth
                        sx={{
                          mt: "16px",
                          py: "10px",
                          fontSize: "16px",
                          fontWeight: 500,
                          textTransform: "none"
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
            {step === 2 && (
              <div
                className={classNames("step", {
                  "step--active": step === 2
                })}
              >
                <div className="step__content">
                  <div
                    className="step__title"
                    onClick={() => {
                      if (platformId) {
                        setStep(2);
                      }
                    }}
                  >
                    Enter credentials
                  </div>
                  {platformId && platformName && (
                    <LoginMobile
                      setLoading={setLoading}
                      closeModal={handleClose}
                      platformId={platformId}
                      platformName={platformName}
                      extra={selectedPlatform}
                      setStep={setStep}
                      resetSocket={() => setConnectMessageAction(null)}
                      afterSubmit={afterSubmit}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Drawer>
  );
};
